import React, { useState } from 'react';
import { apiCall } from './config';
import { useTranslation, LANGUAGES } from './i18n/LanguageContext';
import ImpressumFooter from './components/ImpressumFooter';

function Register() {
  const { t, language, setLanguage } = useTranslation();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    birthdate: '',
    phone: '',
    email: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleBirthdateChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length >= 2) {
      value = value.slice(0, 2) + '.' + value.slice(2);
    }
    if (value.length >= 5) {
      value = value.slice(0, 5) + '.' + value.slice(5, 9);
    }
    setFormData({ ...formData, birthdate: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.firstname.trim()) {
      setError(t('reg.errFirstname'));
      return;
    }
    if (!formData.lastname.trim()) {
      setError(t('reg.errLastname'));
      return;
    }
    if (!formData.birthdate || formData.birthdate.length < 10) {
      setError(t('reg.errBirthdate'));
      return;
    }

    // Konvertiere TT.MM.JJJJ zu TTMMJJJJ
    const parts = formData.birthdate.split('.');
    if (parts.length !== 3) {
      setError(t('reg.errDateFormat'));
      return;
    }
    const birthdateForBackend = parts[0].padStart(2, '0') + parts[1].padStart(2, '0') + parts[2];

    setLoading(true);
    try {
      await apiCall('/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email.trim(),
          firstname: formData.firstname.trim(),
          lastname: formData.lastname.trim(),
          birthdate: birthdateForBackend,
          phone: formData.phone.trim()
        })
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || t('reg.errFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Sprachumschalter (oben rechts)
  const LangSwitcher = () => (
    <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}>
      <button
        type="button"
        className="btn btn-light btn-sm shadow-sm"
        onClick={() => setShowLangMenu(!showLangMenu)}
      >
        {LANGUAGES.find(l => l.code === language)?.flag || '🇩🇪'} ▾
      </button>
      {showLangMenu && (
        <div className="bg-white shadow rounded mt-1" style={{ position: 'absolute', right: 0, minWidth: 160 }}>
          {LANGUAGES.map(lang => (
            <div
              key={lang.code}
              onClick={() => { setLanguage(lang.code); setShowLangMenu(false); }}
              style={{ padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', background: language === lang.code ? '#e8f0fe' : 'transparent' }}
            >
              <span>{lang.flag}</span> <span>{lang.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (success) {
    return (
      <div className="container-fluid vh-100" style={{ position: 'relative', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <LangSwitcher />
        <div className="row justify-content-center pt-5">
          <div className="col-11 col-md-5 col-lg-4">
            <div className="card shadow-lg border-0">
              <div className="card-body p-4 p-md-5 text-center">
                <div style={{fontSize: '4rem'}} className="mb-3">✅</div>
                <h3 className="text-success mb-3">{t('reg.successTitle')}</h3>
                <p className="text-muted mb-3">{t('reg.successText')}</p>
                {/* Ohne WhatsApp-Kontakt keine Einteilung (Leitung, 16.09.2026): Der Bot
                    verknüpft die Nummer mit dem Konto, sobald die vorbereitete Nachricht
                    ankommt – deshalb steht der Name schon drin. */}
                <div className="alert alert-warning text-start" style={{ borderLeftWidth: '6px' }}>
                  <div className="fw-bold mb-1">💬 {t('reg.waTitle')}</div>
                  <div className="small">{t('reg.waText')}</div>
                </div>
                <a
                  href={`https://wa.me/4917643831566?text=${encodeURIComponent(
                    `Hallo, ich habe mich gerade auf gepaeckannahme.de registriert: ${(formData.firstname || '').trim()} ${(formData.lastname || '').trim()}`.trim())}`}
                  target="_blank" rel="noreferrer"
                  className="btn btn-success btn-lg w-100 mb-2"
                >
                  <i className="bi bi-whatsapp me-2"></i>{t('reg.waButton')}
                </a>
                <div className="small text-muted mb-3">{t('reg.waNote')} <strong>+49 176 43831566</strong></div>
                <a href="/" className="btn btn-outline-primary w-100">
                  <i className="bi bi-box-arrow-in-right me-2"></i>
                  {t('reg.toLogin')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid vh-100" style={{ position: 'relative', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <LangSwitcher />
      <div className="row justify-content-center pt-4">
        <div className="col-11 col-md-5 col-lg-4">
          <div className="card shadow-lg border-0">
            <div className="card-body p-4 p-md-5">
              <div className="text-center mb-4">
                <i className="bi bi-person-plus-fill text-primary" style={{fontSize: '3rem'}}></i>
                <h3 className="mt-2 mb-1">{t('reg.title')}</h3>
                <p className="text-muted mb-0">{t('reg.subtitle')}</p>
              </div>

              <div className="alert alert-info py-2 mb-4" style={{fontSize: '0.9rem'}}>
                <i className="bi bi-info-circle-fill me-2"></i>
                <strong>{t('reg.onlyTitle')}</strong><br/>
                <span className="text-muted">
                  {t('reg.onlyText')} <a href="/">{t('reg.homepage')}</a>
                </span>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="firstname" className="form-label">
                    <i className="bi bi-person-fill me-2"></i>{t('reg.firstname')}
                  </label>
                  <input
                    id="firstname"
                    type="text"
                    className="form-control form-control-lg"
                    placeholder={t('reg.firstnamePh')}
                    value={formData.firstname}
                    onChange={e => setFormData({...formData, firstname: e.target.value})}
                    required
                    autoFocus
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="lastname" className="form-label">
                    <i className="bi bi-person-fill me-2"></i>{t('reg.lastname')}
                  </label>
                  <input
                    id="lastname"
                    type="text"
                    className="form-control form-control-lg"
                    placeholder={t('reg.lastnamePh')}
                    value={formData.lastname}
                    onChange={e => setFormData({...formData, lastname: e.target.value})}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="birthdate" className="form-label">
                    <i className="bi bi-calendar-fill me-2"></i>{t('reg.birthdate')}
                  </label>
                  <input
                    id="birthdate"
                    type="text"
                    inputMode="numeric"
                    className="form-control form-control-lg"
                    value={formData.birthdate}
                    onChange={handleBirthdateChange}
                    placeholder="TT.MM.JJJJ"
                    maxLength="10"
                    required
                  />
                  <small className="form-text text-muted">{t('reg.birthdateHint')}</small>
                </div>

                <div className="mb-4">
                  <label htmlFor="phone" className="form-label">
                    <i className="bi bi-telephone-fill me-2"></i>{t('reg.phone')} <span className="text-muted">{t('reg.optional')}</span>
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    className="form-control form-control-lg"
                    placeholder="+49 123 4567890"
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="email" className="form-label">
                    <i className="bi bi-envelope-fill me-2"></i>{t('reg.email')} <span className="text-muted">{t('reg.emailOptional')}</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    inputMode="email"
                    className="form-control form-control-lg"
                    placeholder="name@beispiel.de"
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-success btn-lg w-100 mb-3"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      {t('reg.submitting')}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle-fill me-2"></i>
                      {t('reg.submit')}
                    </>
                  )}
                </button>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {error}
                  </div>
                )}

                <div className="text-center mt-3">
                  <small className="text-muted">
                    {t('reg.alreadyReg')}
                    <a href="/" className="ms-1">{t('reg.toLogin')}</a>
                  </small>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ImpressumFooter />
    </div>
  );
}

export default Register;
